import { FC, Fragment, ReactNode } from "react";
import { DataGraph } from "@/components-v2/DataGraph/DataGraph";

import { observer } from "mobx-react";
import parse from "html-react-parser";
import { useDispatch, useLanguage, useTranslation } from "@/stores/RootStoreContext";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";

import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Alert } from "@/components-v2/Alert";
import { Button, CloseButton, IconButton } from "@/components-v2/Button";
import { useElementId } from "@/utils/reactHelpers";
import { classNames } from "@/utils/classNames";
import { DCEvent } from "@/events/eventsDomain";

import { Icon } from "@/components-v2/Icon";

import { Tabs } from "@/components-v2/Tabs";
import { Tag } from "@/components-v2/Tag";

import { FilePreview } from "@/components-v2/FilePreview";
import { isSupportedFileTypeForPreview } from "@/utils/files";
import { Select, SelectWithNavigator } from "@/components-v2/Select";
import { TMessageKey } from "@/i18n/types.gen";

import { ProjectTemplateDTO, TemplateType } from "@/templates/templateDomain";

import {
  CompanyAdminTemplateActions,
  isMatterShownInPanel,
  isMatterSource,
  prepTemplatePanelBody,
  prepTemplatePanelFooter,
  prepTemplatePanelHeader,
  prepTemplatePanelTabs,
  removeScopeSource,
  ScopeSource,
  scopeSources,
  showMatterInPanel,
  TemplateDetailRow,
  TemplateDetailsTabProps,
  TemplateDocumentFile,
  TemplateDocumentTabProps,
  TemplateMapTabProps,
  TemplateMattersTabProps,
  TemplatePanelBodyProps,
  TemplatePanelFooterProps,
  TemplatePanelHeaderProps,
  TemplateVisibilityProps,
  TemplatePreviewCompanySelectProps,
  TemplateViewContext,
} from "@/templates/templatePanelDomain";
import {
  ProjectTemplateState,
  SelectedTemplateState,
  TemplateState,
} from "@/templates/templateStateDomain";
import { DropdownMenu } from "@/components-v2/Dropdown.tsx";
import { AgendaItemCard, AgendaItemDetails } from "@/meetings/meetingsComponents";

export const TemplatePanel: FC<{
  state: TemplateState;
  selected: SelectedTemplateState;
  context: TemplateViewContext;
}> = observer(({ state, selected, context }) => {
  const dispatch = useDispatch();
  const language = useLanguage();
  return (
    <>
      <VBox borderBottom>
        <TemplatePanelHeader {...prepTemplatePanelHeader(state, selected, context)} />
        <Tabs tabs={prepTemplatePanelTabs(state, language)} />
      </VBox>
      <TemplatePanelBody
        {...prepTemplatePanelBody({ state, selected, context, dispatch, language })}
      />
      <TemplatePanelFooter footer={prepTemplatePanelFooter(state, selected, context)} />
    </>
  );
});

const TemplatePanelHeader: FC<TemplatePanelHeaderProps> = observer(
  ({ title, visibility, options, onClose }) => (
    <Box
      paddingX={"lg"}
      paddingTop={"lg"}
      paddingBottom={"md"}
      justifyContent={"space-between"}
      alignItems={"center"}
      gap={"md"}
      wrap
    >
      <VBox gap={"md"} grow className={"min-w-0 basis-40"}>
        <h4 className={"truncate"}>{title}</h4>
        {visibility && <TemplateVisibility {...visibility} />}
      </VBox>
      <Box gap={"md"} alignItems={"center"} flex={"none"}>
        {visibility && (
          <Button
            variant={"secondary"}
            label={{ key: "enterprise-account.templates.edit-visibility" }}
            onClick={visibility.onEdit}
          />
        )}
        {options && <DropdownMenu {...options} />}
        <CloseButton onClick={onClose} />
      </Box>
    </Box>
  ),
);

const TemplateVisibility: FC<TemplateVisibilityProps> = observer(
  ({ isEnabled, status, groups }) => {
    const { t } = useTranslation();
    return (
      <Box gap={"sm"} alignItems={"center"} textSize={"sm"} wrap>
        <Box gap={"xs"} alignItems={"center"}>
          <Box fg={isEnabled ? "success" : "subtle"}>
            <Icon name={"dot"} weight={"fill"} />
          </Box>
          {t(status)}
        </Box>
        {groups.map((group, index) => (
          <Tag key={index} color={"neutral"} compact label={group} />
        ))}
      </Box>
    );
  },
);

const TemplatePanelBody: FC<TemplatePanelBodyProps> = observer((body) => {
  switch (body.type) {
    case "details":
      return <TemplateDetailsTab {...body.details} />;
    case "document":
      return <TemplateDocumentTab {...body.document} />;
    case "matters":
      return <TemplateMattersTab {...body.matters} />;
    case "map":
      return <TemplateMapTab {...body.map} />;
  }
});

const TemplatePanelFooter: FC<{ footer?: TemplatePanelFooterProps }> = observer(({ footer }) => {
  switch (footer?.type) {
    case TemplateType.PROJECT:
      return footer.scope ? <TemplateScopeFooter {...footer.scope} /> : null;
    case undefined:
      return null;
  }
});

const TemplateDetailsTab: FC<TemplateDetailsTabProps> = observer(({ details, contents }) => (
  <VBox padding gap={"lg"} grow overflowY={"auto"}>
    <TemplateDetailSection label={"enterprise-account.templates.details"} rows={details} />
    <TemplateDetailSection label={"enterprise-account.templates.contents"} rows={contents} />
  </VBox>
));

const TemplateDocumentTab: FC<TemplateDocumentTabProps> = observer(({ recipient, pdf, files }) => {
  const { t } = useTranslation();
  return (
    <>
      {recipient && (
        <Box
          flex={"none"}
          wrap
          gap={"lg"}
          padding
          bg={"base"}
          borderBottom
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Box alignItems={"center"} gap={"sm"}>
            {t("dbot.template-preview.preview-as")}
            <Select {...recipient.previewAs} />
          </Box>
          <Box alignItems={"center"} gap={"sm"}>
            {recipient.recipient && <SelectWithNavigator {...recipient.recipient} />}
          </Box>
        </Box>
      )}
      <VBox padding gap={"lg"} grow overflowY={"auto"} bg={"base"}>
        {pdf ? (
          <FilePreview
            key={pdf.url}
            filename={pdf.filename}
            file={{ url: pdf.url }}
            width={"auto"}
          />
        ) : (
          <LoadingIndicator />
        )}
        {files.map((file) => (
          <TemplateDocumentFilePreview key={file.id} file={file} />
        ))}
      </VBox>
    </>
  );
});

const TemplateMattersTab: FC<TemplateMattersTabProps> = observer((matters) => (
  <VBox gap={"lg"} grow overflowY={"auto"}>
    <TemplateMattersTabView {...matters} />
  </VBox>
));

const TemplateMattersTabView: FC<TemplateMattersTabProps> = observer((matters) => {
  const { t } = useTranslation();
  switch (matters.view) {
    case "loading":
      return <LoadingIndicator />;
    case "unavailable":
      return (
        <Alert type={"info"} message={t("enterprise-account.templates.preview.unavailable")} />
      );
    case "list":
      return (
        <VBox padding gap={"md"}>
          {matters.cards.map((card) => (
            <AgendaItemCard key={card.id} {...card} />
          ))}
        </VBox>
      );
    case "open":
      return (
        <AgendaItemDetails {...matters.card}>
          <TemplateMatterBody rows={matters.rows} text={matters.text} />
        </AgendaItemDetails>
      );
  }
});

const TemplateMatterBody: FC<{ rows: string[][]; text?: string }> = observer(({ rows, text }) => {
  if (rows.length > 0) return <TemplateMatterRows rows={rows} />;
  return text ? (
    <Box fg={"subtle"} className={"whitespace-pre-wrap"}>
      {text}
    </Box>
  ) : null;
});

const TemplateMapTab: FC<TemplateMapTabProps> = observer(({ companySelect, graph }) => (
  <VBox padding gap={"md"} grow overflow={"hidden"}>
    <TemplatePreviewCompanySelect {...companySelect} />
    <TemplateMapTabGraph graph={graph} />
  </VBox>
));

const TemplateMapTabGraph: FC<{ graph: TemplateMapTabProps["graph"] }> = observer(({ graph }) => {
  const { t } = useTranslation();
  switch (graph.view) {
    case "loading":
      return (
        <VBox grow justifyContent={"center"} alignItems={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    case "unavailable":
      return (
        <Alert type={"info"} message={t("enterprise-account.templates.preview.unavailable")} />
      );
    case "graph":
      return (
        <VBox grow overflow={"hidden"}>
          <DataGraph {...graph.props} />
        </VBox>
      );
  }
});

const TemplatePreviewCompanySelect: FC<TemplatePreviewCompanySelectProps> = observer(
  ({ menu, selectPrev, selectNext }) => (
    <Box gap={"sm"}>
      <VBox grow className={"min-w-0"}>
        <DropdownMenu {...menu} />
      </VBox>
      <Button
        fullWidth={false}
        variant={"basic"}
        icon={"caret-left"}
        tooltip={{ key: "previous" }}
        onClick={selectPrev}
        disabled={!selectPrev}
      />
      <Button
        fullWidth={false}
        variant={"basic"}
        icon={"caret-right"}
        tooltip={{ key: "next" }}
        onClick={selectNext}
        disabled={!selectNext}
      />
    </Box>
  ),
);

const scopeBoxClassName = classNames(
  "p-md",
  "bg-default",
  "border",
  "rounded-sm",
  "border-gray",
  "hover:border-gray-hard",
  "focus-within:border-primary",
  "focus-within:ring-1",
);

const TemplateScopeFooter: FC<{
  state: TemplateState;
  actions: CompanyAdminTemplateActions;
  template: ProjectTemplateDTO;
  selected: ProjectTemplateState;
}> = observer(({ state, actions, template, selected }) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const inputId = useElementId();
  const scope = actions.scope;
  if (scope?.templateId !== template.id) return null;
  const sources = scopeSources(template, language);
  return (
    <VBox flex={"none"} borderTop padding gap={"md"}>
      <label htmlFor={inputId}>{t("dbot.template-project.scope")}</label>
      <VBox gap={"md"} className={scopeBoxClassName}>
        <input
          id={inputId}
          className={"outline-hidden"}
          type={"text"}
          autoFocus
          onFocus={(event) => event.target.select()}
          value={scope.projectName.inputValue}
          placeholder={t("dbot.new-project-name.placeholder")}
          onChange={(event) => scope.projectName.setInputValue(event.target.value)}
          disabled={actions.isCreating}
        />
        {sources.length > 0 && (
          <Box wrap gap={"sm"}>
            {sources.map((source) => (
              <ScopeChip
                key={source.id}
                source={source}
                selected={isMatterShownInPanel(state, selected, source.id)}
                onClick={
                  isMatterSource(template, source) ? showMatterInPanel(source.id) : undefined
                }
                onRemove={removeScopeSource(template, source)}
              />
            ))}
          </Box>
        )}
      </VBox>
    </VBox>
  );
});

const ScopeChip: FC<{
  source: ScopeSource;
  selected: boolean;
  onClick?: DCEvent[];
  onRemove: DCEvent[];
}> = observer(({ source, selected, onClick, onRemove }) => {
  const { tf } = useTranslation();
  const dispatch = useDispatch();
  const content = (
    <>
      <Icon name={source.icon} />
      <span>{tf(source.label)}</span>
    </>
  );
  return (
    <Box
      alignItems={"center"}
      border
      rounded
      paddingLeft={"md"}
      padding={"sm"}
      className={classNames("min-w-0", selected && "border-primary")}
      textSize={"sm"}
      bg={source.bg}
    >
      {onClick ? (
        <button
          type={"button"}
          className={"flex items-center gap-2 cursor-pointer"}
          onClick={() => dispatch(onClick)}
        >
          {content}
        </button>
      ) : (
        content
      )}
      <IconButton
        className={"-m-3"}
        color={"gray"}
        icon={"x"}
        tooltip={{ key: "remove" }}
        onClick={onRemove}
      />
    </Box>
  );
});

const TemplateDocumentFilePreview: FC<{ file: TemplateDocumentFile }> = observer(({ file }) =>
  isSupportedFileTypeForPreview(file.filename) ? (
    <FilePreview filename={file.filename} file={{ url: file.url }} width={"auto"} />
  ) : (
    <Box alignItems={"center"} gap={"sm"}>
      <Icon name={"paperclip"} />
      <a href={file.url}>{file.filename}</a>
    </Box>
  ),
);

const TemplateMatterRows: FC<{ rows: string[][] }> = observer(({ rows }) => (
  <TemplateDocumentBody>
    <table style={{ width: "100%" }}>
      <tbody>
        {rows.map((cells, rowIndex) => (
          <tr key={rowIndex}>
            {cells.map((cell, cellIndex) => (
              <Fragment key={cellIndex}>{parse(cell)}</Fragment>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </TemplateDocumentBody>
));

const TemplateDocumentBody: FC<{ children: ReactNode }> = ({ children }) => (
  <VBox container maxWidth={"document"} grow>
    <VBox bg className={"lg:p-12 xl:p-24"}>
      <div className={"revert-all"}>
        <div className={"content"}>{children}</div>
      </div>
    </VBox>
  </VBox>
);

const TemplateDetailSection: FC<{ label: TMessageKey; rows: TemplateDetailRow[] }> = observer(
  ({ label, rows }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"md"}>
        <strong>{t(label)}</strong>
        <Grid cols={12} gap={"md"} alignItems={"start"}>
          {rows.map((row) => (
            <Fragment key={row.label}>
              <GridItem colSpan={5}>{t(row.label)}</GridItem>
              <GridItem colSpan={7} fg={row.value ? undefined : "subtle"}>
                {row.value ? t(row.value) : t("none")}
              </GridItem>
            </Fragment>
          ))}
        </Grid>
      </VBox>
    );
  },
);
